import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getConsent,
  saveConsent,
  applyConsent,
  needsTagManager,
  loadGA4,
  loadGTM,
} from "../lib/cookieConsent";
import "./CookieConsent.css";

// The consent categories shown in the preference center. Each maps to how we
// actually put the cookies to work for the cafe (tracking pixels, analytics,
// session/personalization, retargeting, social sharing and first-party data).
const CATEGORIES = [
  {
    key: "necessary",
    title: "Personalization (Functional & Essential)",
    text:
      "Remember your specific preferences and keep the site working for you — login state, your preferred language or region, and interface choices — so you don't have to reset them on every visit. Persistent logins keep you signed in for up to 30 days unless you log out.",
    always: true,
  },
  {
    key: "analytics",
    title: "Analytics (Performance)",
    text:
      "Count visits and track where our traffic comes from so we can measure and improve the site. Via tools like Google Analytics, these help us see how visitors move around, which pages (like our menu) are most and least popular, and where to fix roadblocks.",
  },
  {
    key: "marketing",
    title: "Marketing (Targeting & Social)",
    text:
      "Let us and our advertising partners show you more relevant ads and measure their performance. These cookies (such as the Meta Pixel) power retargeting, ad frequency capping and social sharing widgets — tracking actions like viewing our menu or completing an order, and may follow your browser across other sites to build interest profiles.",
  },
  {
    key: "forms",
    title: "Simplified Forms (Convenience)",
    text:
      "Store basic first-party information (such as your name or email) locally on your device to securely pre-fill forms — reducing friction and saving you time during reservations, sign-ups or enquiries.",
  },
];

export default function CookieConsent() {
  // view: null (hidden) | "banner" | "prefs"
  const [view, setView] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [prefs, setPrefs] = useState({
    analytics: false,
    marketing: false,
    forms: false,
  });

  const prefsFrom = (c) => ({
    analytics: !!(c && c.analytics),
    marketing: !!(c && c.marketing),
    forms: !!(c && c.forms),
  });

  useEffect(() => {
    setMounted(true);
    const existing = getConsent();
    if (existing) {
      // Replay the stored choice into Consent Mode / dataLayer, then load the
      // trackers the visitor allowed (GA4 for analytics, GTM for analytics/marketing).
      applyConsent(existing);
      if (existing.analytics) loadGA4();
      if (needsTagManager(existing)) loadGTM();
      setPrefs(prefsFrom(existing));
      setView(null);
    } else {
      setView("banner");
    }

    // Let a footer link (or anything) reopen the preference center later.
    window.openCookiePreferences = () => {
      setPrefs(prefsFrom(getConsent()));
      setView("prefs");
    };
    return () => {
      delete window.openCookiePreferences;
    };
  }, []);

  const persist = (analytics, marketing, forms) => {
    const consent = saveConsent({ analytics, marketing, forms });
    if (consent.analytics) loadGA4();
    if (needsTagManager(consent)) loadGTM();
    setView(null);
  };

  const acceptAll = () => persist(true, true, true);
  const rejectOptional = () => persist(false, false, false);
  const savePrefs = () => persist(prefs.analytics, prefs.marketing, prefs.forms);
  const openPrefs = () => {
    setPrefs(prefsFrom(getConsent()));
    setView("prefs");
  };

  if (!mounted || view === null) return null;

  return (
    <div
      className="cc-root"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      {view === "banner" && (
        <div className="cc-modal-overlay">
          <div className="cc-modal cc-modal-intro">
            <h2>We Value Your Privacy &amp; Experience</h2>
            <p className="cc-intro-text">
              When you visit Alfa SouthIn Cafe, we store a small amount of
              information on your browser, mostly in the form of cookies. We use
              these cookies to ensure our site functions properly, to remember
              your preferences, and to understand how you interact with our
              content so we can improve your experience.
            </p>
            <p className="cc-intro-text">
              You can choose to accept all cookies or customize your settings
              below. See our{" "}
              <Link
                to="/cookie-policy"
                className="cc-policy-link"
                onClick={() => setView(null)}
              >
                Cookie Policy
              </Link>{" "}
              for details.
            </p>
            <div className="cc-modal-actions">
              <button
                type="button"
                className="cc-btn cc-btn-ghost"
                onClick={openPrefs}
              >
                Manage Preferences
              </button>
              <button
                type="button"
                className="cc-btn cc-btn-ghost"
                onClick={rejectOptional}
              >
                Reject Optional
              </button>
              <button
                type="button"
                className="cc-btn cc-btn-primary"
                onClick={acceptAll}
              >
                Accept All Cookies
              </button>
            </div>
          </div>
        </div>
      )}

      {view === "prefs" && (
        <div
          className="cc-modal-overlay"
          onClick={() => getConsent() && setView(null)}
        >
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-head">
              <h2>Privacy Preference Center</h2>
              {getConsent() && (
                <button
                  type="button"
                  className="cc-close"
                  aria-label="Close"
                  onClick={() => setView(null)}
                >
                  ×
                </button>
              )}
            </div>
            <p className="cc-modal-lead">
              We use cookies to give you a more personalized and frictionless
              experience. Because we respect your right to privacy, you can
              choose not to allow some types of cookies — though blocking certain
              cookies may impact your experience on the site.
            </p>

            <div className="cc-cats">
              {CATEGORIES.map((cat) => (
                <div className="cc-cat" key={cat.key}>
                  <div className="cc-cat-head">
                    <h3>{cat.title}</h3>
                    {cat.always ? (
                      <span className="cc-always">Always Active</span>
                    ) : (
                      <label className="cc-switch">
                        <input
                          type="checkbox"
                          checked={prefs[cat.key]}
                          onChange={(e) =>
                            setPrefs((p) => ({
                              ...p,
                              [cat.key]: e.target.checked,
                            }))
                          }
                        />
                        <span className="cc-slider" />
                      </label>
                    )}
                  </div>
                  <p>{cat.text}</p>
                </div>
              ))}
            </div>

            <div className="cc-modal-actions">
              <button
                type="button"
                className="cc-btn cc-btn-ghost"
                onClick={rejectOptional}
              >
                Reject All
              </button>
              <button
                type="button"
                className="cc-btn cc-btn-ghost"
                onClick={acceptAll}
              >
                Accept All
              </button>
              <button
                type="button"
                className="cc-btn cc-btn-primary"
                onClick={savePrefs}
              >
                Save My Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
